import { type FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Presentation } from "./Presentation";
import { buildSchema, type ReopenEnqueteFormValues } from "./schema";
import { answersToFormValues, toSubmitAnswers } from "./utils";
import { type Answer, type Props } from "./types";

export const ReopenEnquete: FC<Props> = ({
  questions,
  defaultValues,
  enqueteAnswersUrl,
  csrfToken,
}) => {
  const schema = useMemo(() => buildSchema(questions), [questions]);
  const initialValues = useMemo(
    () => answersToFormValues(defaultValues, questions),
    [defaultValues, questions],
  );

  const formEl = useRef<HTMLFormElement>(null);
  const [submitAnswers, setSubmitAnswers] = useState<Answer[] | null>(null);

  const methods = useForm<ReopenEnqueteFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  const handleOnSubmit = (values: ReopenEnqueteFormValues) => {
    // Enter キー等で submit イベントが再発火しても二重送信しない
    if (submitAnswers !== null) return;

    setSubmitAnswers(toSubmitAnswers(questions, values));
  };

  useEffect(() => {
    if (submitAnswers === null) return;

    formEl.current?.submit();
  }, [submitAnswers]);

  return (
    <FormProvider {...methods}>
      <form
        ref={formEl}
        action={enqueteAnswersUrl}
        method="post"
        aria-label="再開アンケート"
        onSubmit={methods.handleSubmit(handleOnSubmit)}
      >
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        {submitAnswers?.map((answer, index) => (
          <Fragment key={answer.questionCode}>
            <input
              type="hidden"
              name={`reopen_enquete[answers][${index}][question_code]`}
              value={answer.questionCode}
            />
            {answer.optionCodes?.map((optionCode) => (
              <input
                key={optionCode}
                type="hidden"
                name={`reopen_enquete[answers][${index}][option_codes][]`}
                value={optionCode}
              />
            ))}
            {answer.followUpText !== undefined && (
              <input
                type="hidden"
                name={`reopen_enquete[answers][${index}][follow_up_text]`}
                value={answer.followUpText}
              />
            )}
          </Fragment>
        ))}
        <Presentation questions={questions} />
      </form>
    </FormProvider>
  );
};
